import React from 'react';
import { Paper, Grid, TextField, Checkbox } from '@material-ui/core';
import { withReduxForm } from 'utilsModule/form/reduxFormMui';
import { withStyles } from '@material-ui/styles';
import { flatten, pathOr } from 'ramda';
import moment from 'moment';
import { runtimeProps, normalizeImageSrc } from 'appModule/program/components/ConsentForm';

const style = {
    docHeader: {
        width: '100%',
        background: '#ffffff',
        overflow: 'hidden',
    },
    docHeaderBody: {
        minHeight: '118px',
        padding: '18px 48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '24px',
        boxSizing: 'border-box',
    },
    docHeaderCopy: {
        flex: '1 1 0',
        minWidth: 0,
        textAlign: 'center',
    },
    docHeaderCompany: {
        margin: '0 0 8px',
        color: '#4f4f4f',
        fontSize: '13px',
        fontWeight: 600,
        lineHeight: 1.35,
        letterSpacing: '0.2px',
    },
    docHeaderAddress: {
        margin: 0,
        color: '#666666',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.35,
        letterSpacing: '0.2px',
    },
    docHeaderContact: {
        margin: '8px 0 0',
        color: '#7b7b7b',
        fontSize: '11px',
        fontWeight: 500,
        lineHeight: 1.35,
        letterSpacing: '0.2px',
    },
    docHeaderLogoWrap: {
        flex: '0 0 auto',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '190px',
        paddingRight: '18px',
    },
    docHeaderLogoCircle: {
        width: '108px',
        height: '108px',
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff',
    },
    docHeaderLogo: {
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
}

const validate = () => {
    return true;
}

const initialValues = (props) => ({
    ...props.formData
})

const getConfiguration = (props) => {
    const template = { runtimeProps, validate: validate(props), initialValues }

    let config = {
        form: props.formName,
        validate: template.validate,
        enableReinitialize: true,
    };

    let html = [];
    if (props.formTemplate && props.formTemplate.html) {
        const data = { clinicLogoUrl: props.clinicLogoUrl, clinicName: props.formData.clinicName, effectiveDate: props.effectiveDate, devices: props.formData.devices }
        html = runtimeProps.syncHtml(props.formTemplate.html, data)
    }


    return {
        config,
        runtimeProps: template.runtimeProps,
        initialValues: template.initialValues,
        html
    }
}

function withDynamicReduxForm(WrappedComponent) {
    return function (props) {
        const { layout, config, runtimeProps, initialValues, html } = getConfiguration(props);

        const Template = withReduxForm({ layout, config })(withStyles({}, { index: 1 })(WrappedComponent));
        return <Template {...props} parsedHtml={html} runtimeProps={runtimeProps} formData={initialValues(props)} />;
    };
}

class DAFForm extends React.PureComponent {
    _parsedHtml = [];
    constructor(props) {
        super(props)

        this.state = {
            target_signature_ref: null
        }

    }

    _renderDocHeader = () => {
        const { formTemplate, location } = this.props;
        const renderMode = pathOr(null, ['query', 'renderMode'], location);
        if (renderMode === 'pdf') return null;

        const headerImage = pathOr(null, ['documentConfig', 'data', 'header', 'image'], formTemplate);
        const headerSrc = normalizeImageSrc(headerImage);
        const headerTemplate = pathOr(null, ['documentConfig', 'headerTemplate'], formTemplate);
        if (pathOr(false, ['isFallback'], formTemplate)) return null;
        if (headerTemplate && headerSrc) {
            return (
                <div dangerouslySetInnerHTML={{ __html: headerTemplate.replace(/<%=([\w.]+)%>/g, (match, pathString) => {
                    const value = pathOr('', pathString.trim().split('.'), pathOr({}, ['documentConfig', 'data'], formTemplate));
                    if (value && typeof value === 'object' && value.src) return normalizeImageSrc(value);
                    if (/(\.|^)(src|logo)$/i.test(pathString.trim())) return normalizeImageSrc(value);
                    return value !== null && value !== undefined ? `${value}` : '';
                }) }} />
            );
        }

        const headerData = pathOr({}, ['documentConfig', 'data', 'header'], formTemplate);
        const companyLine = headerData.organizationDisplayName || '';
        const address = headerData.organizationAddress || '';
        const contactInfo = headerData.contactInfoLine || '';
        const logo = normalizeImageSrc(headerData.organizationLogo || '');

        if (!companyLine && !address && !contactInfo && !logo) return null;

        return (
            <section style={style.docHeader} role="banner" aria-label="Form header">
                <div style={style.docHeaderBody}>
                    <div style={style.docHeaderCopy}>
                        {companyLine ? <p style={style.docHeaderCompany}>{companyLine}</p> : null}
                        {address ? <p style={style.docHeaderAddress}>{address}</p> : null}
                        {contactInfo ? <p style={style.docHeaderContact}>{contactInfo}</p> : null}
                    </div>
                    {logo ? (
                        <div style={style.docHeaderLogoWrap}>
                            <div style={style.docHeaderLogoCircle}>
                                <img
                                    src={logo}
                                    alt={headerData.organizationName || 'Organization logo'}
                                    style={style.docHeaderLogo}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            </section>
        );
    }

    renderParsedHtml(html, formData) {
        const { t, isReadOnly } = this.props;
        const render = (item) => {
            return flatten(item.map((e) => {
                e.renderChildrens = null
                let result = null;
                if (e.childrens) {
                    e.renderChildrens = render(e.childrens)
                }

                if (e.tag) {
                    if (e.renderChildrens) {
                        result = React.createElement(e.tag, { ...e.props }, null, e.renderChildrens)
                    } else {
                        let isSkipped = false;
                        let element = null;
                        if (e.tag === 'input') {
                            if (e.props.type === 'checkbox') {
                                e.props['checked'] = formData[e.props.name][e.index];
                                element = <Checkbox {...(e.props)} />
                            } else if (e.props.type === 'text') {
                                isSkipped = isReadOnly === true;
                                e.props['value'] = `${formData[e.props.name]}`;
                                element = <TextField {...(e.props)} value={formData[e.props.name]} />
                            }
                        } else if (e.tag === 'img' && e.srcKey) {
                            e.props['src'] = formData[e.srcKey];
                            if (e.useSignaturePad) {
                                e.props['id'] = `${e.srcKey}`
                                element = (
                                    <img
                                        {...e.props}
                                    />)
                            }
                        }

                        result = (!isSkipped) ? (element) ? element : React.createElement(e.tag, { ...e.props }, null) : null;
                    }
                } else if (e.translationKey) {
                    result = (t(e.translationKey, e.translationParams || {}))
                } else {
                    if (e.valueKey && !e.isReadOnly) {
                        result = (formData[e.valueKey])
                    } else if (e.valueType === 'date') {
                        result = moment(e.value).format('DD MMM YYYY')
                    } else result = (e.value)
                }

                if (e.enableHtml) {
                    result = (<div dangerouslySetInnerHTML={{ __html: result }} />)
                }

                return result;
            }))
        }
        const c = render(html)
        return c;
    }
    render() {
        const { formData, runtimeProps, isReadOnly, parsedHtml, _handleClose, openSignaturePad, handleSignaturePad, target_signature_id } = this.props;
        return (
            <React.Fragment>
                <Paper style={{ boxShadow: 'unset' }}>
                    <Grid container justify='center' style={{
                        padding: 0,
                    }}>
                        <Grid style={{ minWidth: '66%' }} item xs={isReadOnly ? 12 : 8}>
                            {this._renderDocHeader()}
                            <div style={{ padding: '25px 40px' }}>
                                {this.renderParsedHtml(parsedHtml, formData)}
                            </div>
                            {/* {this.renderSignatureTable()} */}
                        </Grid>
                    </Grid>
                </Paper>
            </React.Fragment>
        );
    }
}

export default withDynamicReduxForm(DAFForm);
